/** @format */

import { useFormik } from "formik";
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PostIdeaWrapper, useIsMobile } from "senf-atomic-design-system";
import { flyToPolygon } from "senf-atomic-design-system/src/components/atoms/map/utils/flyToPolygon";
import { useModals } from "senf-atomic-design-system/src/components/molecules/modalStack/ModalProvider";
import Success from "senf-atomic-design-system/src/components/organisms/modalContents/success/Success";
import {
  AuthModal,
  parseGeoData,
  postIdea,
  useAuthContext,
  useGlobalState,
  useSchemaValidation,
} from "senf-shared";
import { getIdea } from "../redux/actions/ideaActions";
import { EDIT_IDEA, POST_IDEA } from "../redux/types";

const PostIdeaPage = ({ handle, statefulMap, ideaMap }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { ideaId } = useParams();
  const { setModal, closeModal, openModal } = useModals();
  const { user } = useAuthContext();
  const ideaData = useSelector((state) => state.data.idea);
  const project = useSelector((state) => state.data.project);
  const [index, setIndex] = React.useState(0);
  const [isSubmitting, setSubmitting] = React.useState(false);
  const [statefulCesiumMap, setStatefulCesiumMap] =
    useGlobalState().statefulCesiumMap;

  const [location, setLocation] = useState({});
  const { validation } = useSchemaValidation();

  const addIdeaOptions = [
    ideaMap?.addIdeaOptions?.drawPoint && {
      icon: "pin",
      text: t("dialogMap:add.withLocationShort"),
      tab: "location",
    },
    ideaMap?.addIdeaOptions?.generalIdeas && {
      icon: "pin-strikethrough",
      text: t("dialogMap:add.withoutLocationShort"),
      tab: "noLocation",
    },
    ideaMap?.addIdeaOptions?.drawPath && {
      icon: "path",
      text: t("global:drawPath"),
      tab: "path",
      dataCy: "path",
    },
    ideaMap?.addIdeaOptions?.drawArea && {
      icon: "area",
      text: t("global:drawArea"),
      tab: "area",
      dataCy: "area",
    },
  ].filter(Boolean);
  const [geoDataType, setGeoDataType] = React.useState(
    addIdeaOptions?.[0]?.tab || "location",
  );

  useEffect(() => {
    if (
      !user?.userId ||
      (!user?.profileCompleted &&
        project?.settings?.demographicData?.collect !== false)
    ) {
      openModal(
        <AuthModal
          authAddDetails={user?.userId && !user?.profileCompleted}
          project={project}
        />,
        {
          swipe: !!isMobile,
          size: isMobile && "fullscreen",
          id: "authModal",
        },
      );
    } else {
      closeModal({ id: "authModal" });
      // if (
      //   pathname.indexOf("addIdea") > -1 &&
      //   cookies.get("acceptedRules") !== "true"
      // ) {
      //   openModal(<PostIdeaRules />, {
      //     swipe: !!isMobile,
      //     size: isMobile && "fullscreen",
      //     id: "postIdeaRules",
      //     style: {
      //       backgroundColor: theme.colors.palette["blend-shade-primary-bg"],
      //     },
      //   });
      // }
    }
  }, [user]);

  useMemo(() => {
    if (statefulMap) {
      if (pathname.includes("editIdea") && ideaId) {
        setIndex(1);
        if (ideaData?.long) {
          statefulMap.flyTo({
            center: [ideaData.long, ideaData.lat],
            zoom: 16.5,
            duration: 1300,
            pitch: 30,
          });
        } else if (ideaData?.geoData) {
          flyToPolygon(statefulMap, parseGeoData(ideaData.geoData), 1000);
        }
      }
    }
  }, [statefulMap, pathname, ideaId, ideaData, isMobile]);

  const formik = useFormik({
    initialValues: {
      title: "",
      body: "",
      topic: "",
      address: "",
      contact: null,
      contactTitle: null,
      weblink: null,
      weblinkTitle: null,
    },
    validationSchema: validation([
      { title: t("global:title"), min: 10, max: 70 },
      {
        body: t("global:description"),
        min: ideaMap?.addIdeaOptions?.description?.minChar ?? 50,
        max: ideaMap?.addIdeaOptions?.description?.maxChar ?? 800,
      },
    ]),
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    if (pathname.indexOf("editIdea") > -1 && ideaId && !ideaData && project) {
      dispatch(getIdea(ideaId, project, ideaMap));
    }
  }, [ideaData, project, ideaMap, pathname]);

  useEffect(() => {
    if (ideaData && pathname.indexOf("editIdea") > -1) {
      setGeoDataType(ideaData?.geoDataType);
      const {
        comments,
        ideaId,
        userHandle,
        userImage,
        color,
        geoData,
        ...rest
      } = ideaData;
      if (geoData && geoData !== "") {
        rest.geoData = parseGeoData(geoData);
      }
      formik.setValues({ ...formik.values, ...rest });
    }
  }, [ideaData, pathname]);

  const handleSubmit = (event) => {
    if (isSubmitting) return;
    event.preventDefault();
    setSubmitting(true);

    const map =
      ideaMap?.mapStyle === "cesium" ? statefulCesiumMap : statefulMap;

    postIdea(formik, project, user, location, geoDataType, ideaId, map, ideaMap)
      .then((data) => {
        if (ideaId) data.ideaId = ideaId;
        dispatch({
          type: ideaId ? EDIT_IDEA : POST_IDEA,
          payload: data,
        });
        handle.openAddIdea(false);
        setIndex(0);
        setSubmitting(false);
        formik.resetForm();

        if (ideaId) {
          navigate(`${pathname.split("/").slice(0, -1).join("/")}`);
        } else {
          navigate(
            `${pathname.split("/").slice(0, -1).join("/")}/${data.ideaId}`,
          );
        }
        if (!ideaId) {
          setModal(
            <Success
              project={project}
              ideaMap={ideaMap}
              data={data}
              handle={handle}
              navigate={navigate}
            />,
            {
              id: "successModal",
              swipe: !!isMobile,
            },
          );
        }
      })
      .catch((error) => {
        console.error(error.message);
      });
  };

  return (
    <PostIdeaWrapper
      handle={handle}
      addIdeaOptions={addIdeaOptions}
      statefulMap={statefulMap}
      project={project}
      ideaMap={ideaMap}
      geoDataType={geoDataType}
      formik={formik}
      setLocation={setLocation}
      pathname={pathname}
      handleSubmit={handleSubmit}
      setGeoDataType={setGeoDataType}
      index={index}
      setIndex={setIndex}
      isSubmitting={isSubmitting}
      ideaId={ideaId}
      variant="mainApp"
    />
  );
};

export default PostIdeaPage;
